/**
 * Логика электрики: группы освещения.
 *
 * Управление задаётся явными связями и номером клавиши. Старые выключатели без
 * явного режима сохраняют прежний обход кабелей через светильники.
 */
import type { Cable, ItemsLayer, LightingConnection, WallGraph } from "@/shared/plan";
import type { CatalogItem } from "@/shared/catalog";
import { isLight, isSwitch, getCatalogItem } from "@/shared/catalog";
import { pointInPolygon } from "@/shared/lib/geometry";
import { cableEndPoint, detectRooms, roomOfFace } from "@/widgets/plan";
import { linkedLights } from "./lightingControl";
import { useFixturesStore, useWiringStore } from "@/shared/store/layers";

export { useDrawCable } from "./hooks/useDrawCable";
export { useDrawLightingConnection } from "./hooks/useDrawLightingConnection";
export * from "./groupConnectivity";
export * from "./wiringWarnings";
export * from "./autoWiring";
export * from "./autoWiringPlanner";

/** Тип из каталога для конца кабеля — прибора на стене или светильника на потолке. */
export const cableEndCatalogItem = (electrical: ItemsLayer, id: string): CatalogItem | null => {
	const ref = electrical.fixtures[id]?.ref ?? electrical.items[id]?.ref;
	return ref ? getCatalogItem(ref) : null;
};

/** Соседи по кабелям: id прибора → id приборов, соединённых с ним напрямую. */
const cableNeighbours = (cables: Record<string, Cable>): Map<string, string[]> => {
	const graph = new Map<string, string[]>();
	const link = (a: string, b: string) => {
		const list = graph.get(a);
		if (list) list.push(b);
		else graph.set(a, [b]);
	};
	for (const cable of Object.values(cables)) {
		link(cable.from, cable.to);
		link(cable.to, cable.from);
	}
	return graph;
};

/** Светильники, которыми управляет выключатель: цепочка по кабелям сквозь светильники. */
export const lightsOfSwitch = (
	electrical: ItemsLayer,
	cables: Record<string, Cable>,
	switchId: string,
	connections: Record<string, LightingConnection> = {},
	key?: 1 | 2 | 3,
): string[] => {
	if (electrical.fixtures[switchId]?.lightingControl === "connections") {
		return linkedLights(connections, switchId, key).filter((id) =>
			isLight(cableEndCatalogItem(electrical, id)),
		);
	}
	if (key !== undefined && key !== 1) return [];
	const neighbours = cableNeighbours(cables);
	const seen = new Set<string>([switchId]);
	const lights: string[] = [];
	const queue = [switchId];
	while (queue.length > 0) {
		const current = queue.shift() as string;
		for (const next of neighbours.get(current) ?? []) {
			if (seen.has(next)) continue;
			seen.add(next);
			if (!isLight(cableEndCatalogItem(electrical, next))) continue;
			lights.push(next);
			queue.push(next);
		}
	}
	return lights;
};

export const addLightingLink = (
	electrical: ItemsLayer,
	cables: Record<string, Cable>,
	switchId: string,
	lightId: string,
	style: LightingConnection["style"],
): string | null => {
	const wiring = useWiringStore.getState();
	const previous =
		electrical.fixtures[switchId]?.lightingControl === "connections"
			? []
			: lightsOfSwitch(electrical, cables, switchId, wiring.connections);
	const id = wiring.addLightingConnection(switchId, lightId, style);
	if (id)
		for (const previousLight of previous)
			if (previousLight !== lightId) wiring.addLightingConnection(switchId, previousLight, style);
	return id;
};

export const preserveCableLighting = (
	electrical: ItemsLayer,
	cables: Record<string, Cable>,
	style: LightingConnection["style"],
) => {
	const wiring = useWiringStore.getState();
	for (const fixture of Object.values(electrical.fixtures)) {
		if (fixture.lightingControl === "connections" || !isSwitch(getCatalogItem(fixture.ref)))
			continue;
		const lights = lightsOfSwitch(electrical, cables, fixture.id, wiring.connections);
		for (const light of lights) wiring.addLightingConnection(fixture.id, light, style);
		if (lights.length)
			useFixturesStore.getState().updateFixture(fixture.id, { lightingControl: "connections" });
	}
};

/** Выключатели, в группу которых входит светильник. Пусто — свет некому включить. */
export const switchesOfLight = (
	electrical: ItemsLayer,
	cables: Record<string, Cable>,
	lightId: string,
	connections: Record<string, LightingConnection> = {},
): string[] =>
	Object.keys(electrical.fixtures)
		.filter((id) => isSwitch(cableEndCatalogItem(electrical, id)))
		.filter((id) => lightsOfSwitch(electrical, cables, id, connections).includes(lightId));

/**
 * Недостающие связи «выключатель → свет» по комнатам: каждый светильник получает
 * ближайший выключатель на грани той же комнаты. Уже заданные вручную пары не дублируем.
 */
export const autoLightingConnections = (
	graph: WallGraph,
	electrical: ItemsLayer,
	existing: Record<string, LightingConnection>,
	style: LightingConnection["style"],
): { from: string; to: string; style: LightingConnection["style"] }[] => {
	const rooms = detectRooms(graph);
	const switches = Object.values(electrical.fixtures).filter((fixture) =>
		isSwitch(getCatalogItem(fixture.ref)),
	);
	const linked = new Set(
		Object.values(existing).map((connection) =>
			[connection.from, connection.to].toSorted().join(":"),
		),
	);
	const pairs: { from: string; to: string; style: LightingConnection["style"] }[] = [];

	for (const light of Object.values(electrical.items)) {
		const room = rooms.find((candidate) => pointInPolygon(light, candidate.inner));
		if (!room) continue;
		const nearest = switches
			.filter(
				(fixture) =>
					roomOfFace(graph, rooms, { wallId: fixture.wallId, side: fixture.side })?.id === room.id,
			)
			.map((fixture) => ({ fixture, point: cableEndPoint(graph, electrical, fixture.id) }))
			.filter(
				(candidate): candidate is { fixture: (typeof switches)[number]; point: typeof light } =>
					candidate.point !== null,
			)
			.toSorted(
				(a, b) =>
					Math.hypot(a.point.x - light.x, a.point.y - light.y) -
					Math.hypot(b.point.x - light.x, b.point.y - light.y),
			)[0]?.fixture;
		if (!nearest) continue;
		const key = [nearest.id, light.id].toSorted().join(":");
		if (linked.has(key)) continue;
		linked.add(key);
		pairs.push({ from: nearest.id, to: light.id, style });
	}
	return pairs;
};
